// src/components/catalog/OpenToolDialog.jsx
//
// Shown only for tools that have a desktop build. Everything else opens on the
// first click, as it always has — this dialog exists to ask a question, so it
// must never appear when there's nothing to ask.

import React, { useState } from 'react';
import { Download, ExternalLink, Loader2 } from 'lucide-react';
import ToolIcon from '../common/ToolIcon';
import { Modal, cx } from '../common/ui';
import { tileColor } from '../../services/toolIcons';
import {
  buildsOf,
  detectPlatform,
  downloadDesktopBuild,
  formatSize,
} from '../../services/desktop';

const Choice = ({ icon: Icon, title, meta, badge, busy, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className={cx(
      'flex w-full items-center gap-3 rounded-lg border border-line-strong bg-surface px-3.5 py-3 text-left transition-colors',
      'hover:border-accent-line hover:bg-accent-soft disabled:cursor-wait disabled:opacity-60'
    )}
  >
    <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-surface-2 text-ink-2">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
    </span>

    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2">
        <span className="text-sm font-medium">{title}</span>
        {badge && (
          <span className="rounded-full bg-accent-soft px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-accent">
            {badge}
          </span>
        )}
      </span>
      {meta && <span className="mt-0.5 block truncate text-xs text-ink-3">{meta}</span>}
    </span>
  </button>
);

const OpenToolDialog = ({ tool, category, platforms, onOpenWeb, onClose }) => {
  const builds = buildsOf(tool);
  const thisMachine = detectPlatform(platforms);
  const [busyPlatform, setBusyPlatform] = useState(null);
  const [error, setError] = useState('');

  // Whatever this laptop runs goes first — but every build stays listed, since
  // people do download the other one for a machine they're setting up.
  const available = platforms
    .filter((platform) => builds[platform.key])
    .sort((a, b) => (a.key === thisMachine ? -1 : b.key === thisMachine ? 1 : 0));

  const download = async (platform) => {
    setBusyPlatform(platform.key);
    setError('');
    try {
      await downloadDesktopBuild(tool._id, platform.key);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'That download could not be started. Try again.');
      setBusyPlatform(null);
    }
  };

  return (
    <Modal title={tool.name} onClose={onClose}>
      <div className="mb-5 flex items-center gap-3">
        <span className="cat-tile h-10 w-10" data-color={tileColor(tool, category)}>
          <ToolIcon tool={tool} fill className="h-5 w-5" />
        </span>
        <p className="text-[13.5px] leading-relaxed text-ink-2">{tool.description}</p>
      </div>

      <p className="mb-2.5 text-[13px] font-medium text-ink">How do you want to open it?</p>

      {error && (
        <p className="mb-2.5 rounded-lg bg-danger-soft px-3 py-2.5 text-[13px] text-danger">{error}</p>
      )}

      <div className="flex flex-col gap-2">
        <Choice
          icon={ExternalLink}
          title="Open in browser"
          meta={tool.url.replace(/^https?:\/\//, '')}
          onClick={() => {
            onOpenWeb(tool);
            onClose();
          }}
        />

        {available.map((platform) => {
          const build = builds[platform.key];
          const parts = [
            build.fileName.slice(build.fileName.lastIndexOf('.')),
            build.version && `v${build.version}`,
            formatSize(build.size),
          ].filter(Boolean);

          return (
            <Choice
              key={platform.key}
              icon={Download}
              title={platform.action}
              meta={parts.join(' · ')}
              badge={platform.key === thisMachine ? 'Your device' : ''}
              busy={busyPlatform === platform.key}
              onClick={() => download(platform)}
            />
          );
        })}
      </div>

      <p className="mt-4 text-xs text-ink-3">
        The desktop app signs in with the same account. You can always come back here and open it
        in the browser instead.
      </p>
    </Modal>
  );
};

export default OpenToolDialog;
